import { $isLineBreakNode, $isParagraphNode, $isTextNode } from 'lexical';
import { $isBeautifulMentionNode } from 'lexical-beautiful-mentions';
import { $isColorTextNode } from './nodes/ColorTextNode';

/**
 * Lexical EditorState -> chat WIRE STRING.
 *
 * This is the load-bearing contract between the composer and the rest of the
 * app. The wire string this produces is byte-for-byte what the old <textarea>
 * value used to be: it is handed to onSendMessage / onTyping unchanged, queued
 * by socketService, stored by the backend, and rendered back by
 * renderRichText() / scanned by containsMentionOf() (chat/src/utils/richText.js).
 * Those consumers are NOT changing, so this serializer must emit exactly their
 * input format.
 *
 * ── CANONICAL OUTPUT RULES (the test fixtures assert these verbatim) ─────────
 *
 *  Markers, applied INSIDE-OUT in this fixed order so output is deterministic:
 *      1. underline   ~text~
 *      2. italic      *text*        (always `*`, never `_`, on write)
 *      3. bold        **text**
 *      4. color       [#rrggbb|text]   ← OUTERMOST, wraps the format markers
 *                                        e.g. bold+color  -> [#ff0000|**x**]
 *
 *  Mentions      ->  @[displayName](userId)   (the pill's structured fields)
 *  Line breaks   ->  \n   (Shift+Enter within a paragraph = LineBreakNode)
 *  Paragraphs    ->  joined with \n (a blank line is just an empty paragraph)
 *
 *  The message-wide DEFAULT color (currentColor / onColorChange) is a SEPARATE
 *  message field — it is NEVER folded into this string. Only inline color spans
 *  (ColorTextNode with an explicit __color) emit [#hex|…].
 *
 *  Adjacent text runs with identical formatting are coalesced before wrapping,
 *  so two same-format nodes become `**ab**`, not `**a****b**`.
 *
 *  serialize() returns the FAITHFUL string (no trimming). The caller
 *  (ChatPanel.handleSendMessage) applies .trim() at send, matching the old
 *  `messageText.trim()` behaviour.
 */

// Wrap a plain string in the format markers a homogeneous run carries.
function wrapRun(text, format, color) {
  let s = text;
  if (format.underline) s = `~${s}~`;
  if (format.italic) s = `*${s}*`;
  if (format.bold) s = `**${s}**`;
  if (color) s = `[${color}|${s}]`;
  return s;
}

// The formatting signature of a text node, used both to wrap it and to decide
// whether two adjacent nodes can be coalesced into one run.
function runSignature(node) {
  return {
    bold: node.hasFormat('bold'),
    italic: node.hasFormat('italic'),
    underline: node.hasFormat('underline'),
    color: $isColorTextNode(node) ? node.getColor() || null : null,
  };
}

function sameSignature(a, b) {
  return (
    a.bold === b.bold &&
    a.italic === b.italic &&
    a.underline === b.underline &&
    a.color === b.color
  );
}

// Serialize one block (paragraph) by walking its inline children, coalescing
// consecutive text runs that share a formatting signature.
function serializeBlock(block) {
  const children = block.getChildren();
  let out = '';
  let pending = null; // { text, sig }

  const flush = () => {
    if (pending) {
      out += wrapRun(pending.text, pending.sig, pending.sig.color);
      pending = null;
    }
  };

  for (const child of children) {
    if ($isBeautifulMentionNode(child)) {
      flush();
      // value = display username; data.id = stable userId attached in onSearch.
      const value = child.getValue();
      const data = child.getData() || {};
      out += `@[${value}](${data.id != null ? data.id : ''})`;
      continue;
    }
    if ($isLineBreakNode(child)) {
      flush();
      out += '\n';
      continue;
    }
    if ($isTextNode(child)) {
      const sig = runSignature(child);
      const text = child.getTextContent();
      if (pending && sameSignature(pending.sig, sig)) {
        pending.text += text;
      } else {
        flush();
        pending = { text, sig };
      }
      continue;
    }
    // Unknown inline node: emit its text content unformatted, defensively.
    flush();
    out += child.getTextContent();
  }
  flush();
  return out;
}

/**
 * Serialize the whole editor root to a wire string. Must be called inside an
 * editorState.read(...) (or editor.getEditorState().read(...)) so the $ helpers
 * have an active read context.
 *
 * @param {import('lexical').RootNode} root  ($getRoot())
 * @returns {string}
 */
export function serializeToWire(root) {
  const blocks = root.getChildren().filter($isParagraphNode);
  // Fallback: if the root somehow holds non-paragraph top-level nodes, still
  // serialize their text so nothing is silently dropped.
  if (blocks.length === 0) {
    return root.getTextContent();
  }
  return blocks.map(serializeBlock).join('\n');
}
